import React, { useMemo } from 'react';
import styled from 'styled-components';
import StaffShifts from '../pages/kiosk/schedule/staff/StaffShifts';
import PanelContainer from './PanelContainer';
import PatientSlot from '../patient/PatientSlot/PatientSlot';
import { keyframes } from 'styled-components';
import PanelEmpty from './PanelEmpty';
import Carousel from '../carousel/Carousel';
import { PACU, POST_OP, PRE_OP, WAITING_ROOM } from '../entities/room/enums';
import { getFrameColor, getLogEntries, isPreOpAsDPU } from '../pages/kiosk/tablet/utils';
import { mockStaff } from '@/components/pages/kiosk/monitor/staff.mock';

const Slots = styled.span`
  display: flex;
  flex-flow: column;
  grid-row-gap: 0.25rem;
  padding: 0 1rem;
  margin: ${props => (props.patients > 0 ? 'initial' : 'auto')};
  margin-left: -1rem;
  margin-right: -1rem;
`;

// CSS animation replacements for react-pose
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PatientSlotItem = styled(PatientSlot).attrs({
  'data-patient-slot': 'true',
})`
  animation: ${fadeInUp} 300ms ease forwards;
  margin-bottom: 0.25rem;
`;

const PanelEmptyItem = styled(PanelEmpty)`
  animation: ${fadeInUp} 500ms ease forwards;
`;

const Panel = ({
  roomType,
  name,
  capacity,
  patients,
  patientsCount,
  image,
  highlightReady,
  showPriority,
  showPreOpNote,
  showPacuNote,
  showBed,
  screenType,
  carouselSize,
  fusePanels,
  lastInRow,
  showEmptyContent,
  preOpFocused,
  pacuFocused,
  messagingView,
  staffMode,
  showLatePreOpIndicator,
  showPatientNotesOnInternalScreen,
  isSuperAdmin,
  roomStaff,
  isGroupAdmin,
  isKiosk = true,
  showPatientAgeAndSex,
  showPatientProcedureTimes,
  showPatientScheduledOr,
}) => (
  <PanelContainer
    fusePanels={fusePanels}
    lastInRow={lastInRow}
    title={name}
    capacity={capacity}
    patients={patientsCount}
    messagingView={messagingView}
    roomStaff={roomStaff}
  >
    {staffMode ? (
      <StaffShifts isFooter={true} staffShifts={mockStaff} isKiosk={true} editableStaff={false} type="RoomShift" />
    ) : (
      <Slots patients={(patients && patients.length) || 0} $isKiosk={isKiosk}>
        {patients && patients.length > 0 ? (
          <Carousel size={carouselSize ?? 5} interval={7000}>
            {patients.map(patient => (
              <PatientSlotWrapper
                key={patient.id}
                patient={patient}
                showPriority={showPriority}
                showPreOpNote={showPreOpNote}
                showPacuNote={showPacuNote}
                showBed={showBed}
                roomType={roomType}
                highlightReady={highlightReady}
                screenType={screenType}
                preOpFocused={preOpFocused}
                ptComplete={patient.ptComplete}
                messagingView={messagingView}
                showLatePreOpIndicator={showLatePreOpIndicator}
                showPatientNotesOnInternalScreen={showPatientNotesOnInternalScreen}
                isSuperAdmin={isSuperAdmin}
                isGroupAdmin={isGroupAdmin}
                showPatientAgeAndSex={showPatientAgeAndSex}
                showPatientProcedureTimes={showPatientProcedureTimes}
                showPatientScheduledOr={showPatientScheduledOr}
              />
            ))}
          </Carousel>
        ) : (
          showEmptyContent && <PanelEmptyItem image={image} text="Waiting for patients…" />
        )}
      </Slots>
    )}
  </PanelContainer>
);

const PatientSlotWrapper = ({
  patient,
  showPriority,
  showPreOpNote,
  showPacuNote,
  showBed,
  roomType,
  highlightReady,
  screenType,
  preOpFocused,
  messagingView,
  showLatePreOpIndicator,
  showPatientNotesOnInternalScreen,
  isSuperAdmin,
  showPatientAgeAndSex,
  showPatientProcedureTimes,
  showPatientScheduledOr,
}) => {
  const isDPU = useMemo(() => isPreOpAsDPU(roomType, getLogEntries(patient)), [patient, roomType]);
  const frameColor = useMemo(() => getFrameColor(roomType, patient.log), [patient.log, roomType]);
  const timeEntered = useMemo(() => new Date(patient.timeEntered), [patient.timeEntered]);

  return (
    <PatientSlotItem
      caretakerMessage={patient.caretakerMessages || null}
      status={patient.monitorStatus}
      procedure={patient.procedureType?.name}
      physician={patient.physician?.name}
      patient={patient.name}
      patientId={patient.id}
      patientType={patient.type}
      patientData={patient}
      room={{ type: roomType }}
      bed={showBed ? (roomType === PACU ? patient.pacuBed : patient.bed) : undefined}
      showPriority={showPriority}
      priority={patient.priority}
      showPreOpNote={showPreOpNote}
      showPacuNote={showPacuNote}
      showPatientNote={roomType === WAITING_ROOM && showPatientNotesOnInternalScreen}
      preOpNote={patient.preOpNote}
      pacuNote={patient.pacuNote}
      patientNote={patient.note}
      timeEntered={timeEntered}
      ready={highlightReady && patient.ready}
      readyForSurgeon={patient.readyForSurgeon}
      readyForOr={patient.readyForOr}
      familyReady={
        roomType === PACU ? patient?.familyReadyPACU : roomType === POST_OP ? patient?.familyReadyPOSTOP : undefined
      }
      frameColor={frameColor}
      isHoldProcedure={patient.isHoldProcedure}
      isBlockNerve={patient.isBlockNerve}
      expectedExitTime={patient.expectedExitTime}
      screenType={screenType}
      preOpFocused={preOpFocused}
      hideChart={true}
      isDPU={isDPU}
      blockNerveFollow={patient.blockNerveFollow}
      called={patient.called}
      transferredToChair={patient.transferredToChair}
      transferredToPT={patient.transferredToPT}
      ptComplete={patient.ptComplete}
      isPreOp={roomType === PRE_OP}
      isPacu={roomType === PACU}
      messagingView={messagingView}
      readyForAnesthesia={patient.readyForAnesthesia}
      isSpinalTimeout={patient.isSpinalTimeout}
      showLatePreOpIndicator={showLatePreOpIndicator}
      isSuperAdmin={isSuperAdmin}
      showPatientAgeAndSex={showPatientAgeAndSex}
      showPatientProcedureTimes={showPatientProcedureTimes}
      showPatientScheduledOr={showPatientScheduledOr}
    />
  );
};

export default Panel;
